// Small stand-ins for the Next.js APIs the ported components use (Link, useRouter,
// usePathname, useSearchParams), backed by TanStack Router. They keep the ported
// components close to the originals.
import { forwardRef, type AnchorHTMLAttributes } from "react";
import { Link as RouterLink, useRouterState } from "@tanstack/react-router";
import { go, parseHref } from "./nav";
import { queryClient } from "./query";

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: string;
  prefetch?: boolean;
  replace?: boolean;
  scroll?: boolean;
};

/** next/link: app paths go through the router (with preloading), anything else is a plain <a>. */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, prefetch, replace, scroll, ...rest },
  ref,
) {
  void scroll;
  if (!href.startsWith("/") || href.startsWith("//") || href.startsWith("/api/")) {
    return <a ref={ref} href={href} {...rest} />;
  }
  const { to, search, hash } = parseHref(href);
  return (
    <RouterLink
      ref={ref}
      to={to}
      search={search}
      hash={hash}
      replace={replace}
      preload={prefetch === false ? false : "intent"}
      {...rest}
    />
  );
});

export default Link;

/** next/navigation useRouter. */
export function useRouter() {
  return {
    push: (href: string) => void go(href),
    replace: (href: string) => void go(href, { replace: true }),
    back: () => history.back(),
    refresh: () => void queryClient.invalidateQueries(),
  };
}

/** next/navigation usePathname. */
export function usePathname() {
  return useRouterState({ select: (s) => s.location.pathname });
}

/** next/navigation useSearchParams (read-only). */
export function useSearchParams() {
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  return new URLSearchParams(searchStr);
}
